/**
 * NamedWindowSection — renderer + editor for the standalone `WINDOW w AS (…)`
 * clause (`WINDOW w AS (…), w2 AS (…)`). Mounted ABOVE OrderBySection in
 * SelectRenderer when `outline.window` is non-null (BUG 3b — the WINDOW clause
 * logically precedes ORDER BY and must be on-screen without scrolling).
 *
 * Each definition is a `NamedWindowItem` with REAL rename / remove / structured
 * body editing (ANA-03); the footer offers a real add affordance (ANA-03). A
 * quiet, truthful capability hint (ANA-01/02) names the minimum SQL Server /
 * Oracle version that gained named windows — never a false "unsupported".
 */
import { useState } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addNamedWindow } from '../../edit/edits/addNamedWindow';
import { useVqeStore } from '../../store/vqeStore';
import { ENGINE_DISPLAY_NAME, windowClauseMinVersionHint } from '../../dialect/statementCapabilities';
import { vqeCopy } from '../../ux/vqeCopy';
import { AddButton } from '../common/AddButton';
import { Button } from '../../../../../components/ui/button';
import { ClauseCard } from './ClauseCard';
import type { SelectOutline } from './extractSelectOutline';
import { extractNamedWindows, type NamedWindowDef } from './extractWindowFunction';
import { NamedWindowItem } from './NamedWindowItem';

export interface NamedWindowSectionProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly statementId: string;
  readonly editContext?: VqeEditContext;
}

// Suggest a fresh, non-colliding window name (`w`, `w2`, `w3`, …).
function suggestName(defs: readonly NamedWindowDef[]): string {
  const taken = new Set(defs.map((d) => d.name.toLowerCase()));
  if (!taken.has('w')) return 'w';
  let n = 2;
  while (taken.has(`w${n}`)) n += 1;
  return `w${n}`;
}

export function NamedWindowSection({ bufferText, outline, options, statementId, editContext }: NamedWindowSectionProps) {
  const { track } = useAnalytics();
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState('');

  if (!outline.window) return null;
  const defs = extractNamedWindows(bufferText, outline.window.start, outline.window.end, options);
  const engine = options.engine;
  const editReady = Boolean(editContext) && Boolean(editContext?.service.getSchemaSnapshot());
  const minVersion = windowClauseMinVersionHint(engine);

  const startAdd = (): void => {
    setAddName(suggestName(defs));
    setAdding(true);
  };
  const commitAdd = (): void => {
    const trimmed = addName.trim();
    if (!editContext || trimmed.length === 0) { setAdding(false); return; }
    setAdding(false);
    track('feature:vqe_named_window_edit', { action: 'add', engine });
    void addNamedWindow(trimmed, '', toCommitCtx(editContext)).catch((error: unknown) => {
      useVqeStore.getState().recordCommitFeedback({
        kind: 'failure',
        at: Date.now(),
        message: error instanceof Error ? error.message : String(error),
      });
    });
  };

  const footer = adding && editReady ? (
    <span className="flex items-center gap-2" data-testid="vqe-window-add-form">
      <input
        autoFocus
        type="text"
        value={addName}
        onChange={(e) => setAddName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
          else if (e.key === 'Escape') { e.preventDefault(); setAdding(false); }
        }}
        aria-label="New window name"
        data-testid="vqe-window-add-name"
        className="w-24 rounded border border-input bg-background px-1.5 py-0.5 font-mono text-[11px]"
      />
      <Button type="button" variant="secondary" size="sm" className="h-6 px-2 text-[11px]" onClick={commitAdd} data-testid="vqe-window-add-confirm">
        {vqeCopy.actions.addWindow}
      </Button>
    </span>
  ) : (
    <AddButton
      label={vqeCopy.actions.addWindow}
      testId="vqe-window-add"
      disabled={!editReady}
      tooltip={vqeCopy.disabled.windowStructuredUnavailable}
      onClick={startAdd}
    />
  );

  return (
    <ClauseCard title="Window" testId="vqe-window-section" footer={footer}>
      {minVersion ? (
        <p className="mb-2 text-[11px] italic text-muted-foreground" data-testid="vqe-window-capability-hint">
          {vqeCopy.capability.window.versionHint(ENGINE_DISPLAY_NAME[engine], minVersion)}
        </p>
      ) : null}
      {defs.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid="vqe-window-empty">
          No named windows parsed.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="vqe-window-items">
          {defs.map((def, idx) => (
            <NamedWindowItem
              key={`${def.name}-${idx}`}
              def={def}
              index={idx}
              statementId={statementId}
              testId="vqe-window-item"
              options={options}
              editContext={editContext}
            />
          ))}
        </ul>
      )}
    </ClauseCard>
  );
}
